import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  useAcceptMakeupRequest,
  useDeclineMakeupRequest,
} from '../../api/generated/scheduling/scheduling';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatInPersonTimezone } from '../../i18n/datetime';
import { useAllMakeupRequests } from './makeupRequests';
import styles from './makeup.module.css';

/**
 * Resposta do professor a um pedido de reposicao.
 *
 * A tela respondia **no escuro**: nao mostrava de quem era o pedido, nem a que
 * horario o aluno tinha pedido, nem ate quando a janela ficava aberta — e o
 * aceite enviava `Date.now() + 24 h`, um instante que ninguem escolheu, cravado
 * no codigo.
 *
 * **O `startsAtUtc` do corpo CONFIRMA o horario do pedido; ele nao propoe
 * outro.** Este paragrafo dizia o contrario — que o servidor descartava o campo,
 * apontando uma linha de `scheduling/handler.go` que nao existe mais — e uma
 * promessa dessas e pior do que nenhuma: quem revisasse a tela leria que o
 * `startsAtUtc` enviado nao tem consequencia e pararia de procurar. Hoje
 * `AcceptMakeupRequest` le o corpo (`handler.go`, `AcceptMakeupAt(request.Body
 * .StartsAtUtc)`) e o servico compara com o horario pedido: divergencia e 409
 * (`ErrMakeupStartMismatch`, scheduling/service.go), corpo ausente ou instante
 * zerado e 400, e horario ja vencido tambem e 409. O aceite deixou de ser um
 * carimbo — ele cria a aula de reposicao naquele instante.
 *
 * E por isso que a unica origem legitima do campo aqui e
 * `request.requestedStartsAtUtc`, e que o botao fecha quando ele e nulo: sem
 * pedido nao ha o que confirmar, e qualquer valor fabricado vira 409 na cara do
 * professor — ou, pior, a aula marcada as tantas da madrugada.
 *
 * Nao ha leitura unitaria de pedido no contrato: a lista que a tela anterior ja
 * consome e a fonte, e o cache do react-query a serve sem segunda ida. A lista e
 * percorrida ATE O FIM (ver `useAllMakeupRequests`) — buscar so a primeira pagina
 * transformava todo pedido fora das 50 mais recentes em "Nao foi possivel
 * carregar", sem saida no produto.
 */
export function MakeupRespondPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { makeupRequestId = '' } = useParams();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const listQuery = useAllMakeupRequests();
  const accept = useAcceptMakeupRequest();
  const decline = useDeclineMakeupRequest();
  const [reason, setReason] = useState('');

  const request = listQuery.items?.find((item) => item.id === makeupRequestId);

  const done = (): void => {
    void navigate('/makeup', { replace: true });
  };

  if (!me) return null;

  const failure = [accept.error, decline.error].find((error) => error !== null) ?? null;
  const message =
    failure === null
      ? null
      : isProblemError(failure) && failure.status === 409
        ? t('makeup.conflict')
        : t('error.unexpected');

  // Enquanto o cursor nao acabou, "nao achei" nao e uma resposta: seguir a fila
  // ate o fim e o que separa "este pedido nao existe" de "este pedido ainda nao
  // chegou".
  if (listQuery.isPending) return null;
  if (request === undefined) {
    return (
      <div className={styles.page}>
        <h1>{t('makeup.respondTitle')}</h1>
        <Alert tone="error">{t('error.loadFailed')}</Alert>
      </div>
    );
  }

  const requestedAt = request.requestedStartsAtUtc;

  return (
    <div className={styles.page}>
      <h1>{t('makeup.respondTitle')}</h1>

      {/*
        Quem pediu, para quando e ate quando ha prazo. Sem isto o professor
        aceitava ou recusava sem saber o que estava respondendo — e o `state`
        aparece porque um pedido ja respondido devolve 409, e ver o estado antes
        do clique e mais barato que descobrir depois.
      */}
      <p>
        {request.student.displayName} · {t(`makeup.state.${request.state}`)}
      </p>
      <p>
        {t('makeup.requestedAt')}:{' '}
        {requestedAt === null ? '—' : formatInPersonTimezone(requestedAt, me.timezone, me.locale)}
      </p>
      <p>
        {t('makeup.windowCloses', {
          date: formatInPersonTimezone(request.windowClosesAt, me.timezone, me.locale),
        })}
      </p>

      {message !== null ? <Alert tone="warning">{message}</Alert> : null}

      {/*
        Sem horario pedido nao ha aceite: o contrato exige `startsAtUtc` e a
        unica origem legitima dele nesta tela e o pedido do aluno. Inventar um
        valor para preencher o campo obrigatorio e o que a tela fazia. O caso e
        quase inalcancavel — `createMakeupRequest` exige o horario —, e falhar
        FECHADO aqui deixa a pendencia com nome em vez de escondida.
      */}
      <Button
        variant="primary"
        loading={accept.isPending}
        disabled={requestedAt === null}
        onClick={() => {
          if (requestedAt === null) return;
          accept.mutate({ makeupRequestId, data: { startsAtUtc: requestedAt } }, { onSuccess: done });
        }}
      >
        {t('makeup.accept')}
      </Button>

      {/*
        O motivo da recusa era `reason.trim() || '-'`: o `minLength: 1` do
        contrato ficava satisfeito e a trilha de auditoria recebia um traco. O
        campo passa a ser obrigatorio de verdade — botao fechado enquanto vazio —
        e nada mais e fabricado no lugar do que o professor nao escreveu.
      */}
      <Field
        label={t('makeup.declineReason')}
        name="reason"
        required
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <Button
        variant="secondary"
        loading={decline.isPending}
        disabled={reason.trim().length === 0}
        onClick={() =>
          decline.mutate(
            { makeupRequestId, data: { reason: reason.trim() } },
            { onSuccess: done },
          )
        }
      >
        {t('makeup.decline')}
      </Button>
    </div>
  );
}
